import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Group, List, Modal, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

type PreShareWarningModalProps = {
  opened: boolean;
  title: string;
  confirmLabel: string;
  isSharing: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

const CLIENT_DATA_TO_LOOK_FOR = [
  'Real names',
  'Addresses',
  'Dates of birth',
  'National Insurance numbers',
  'Policy or account numbers',
  "Anything about your firm's private arrangements",
];

export default function PreShareWarningModal({
  opened,
  title,
  confirmLabel,
  isSharing,
  errorMessage,
  onClose,
  onConfirm,
}: PreShareWarningModalProps) {
  const [hasChecked, setHasChecked] = useState(false);

  // The tick is the only safeguard on this path, so it must not survive the modal closing:
  // a second skill opened after a first was confirmed would inherit a tick nobody gave it.
  useEffect(() => {
    if (!opened) setHasChecked(false);
  }, [opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg" centered>
      <Stack gap="md">
        <Text size="sm">
          This publishes the skill to the skill library, where any company using Afternoon can read
          it and add it to their own Siesta.
        </Text>

        <Alert
          variant="outline"
          color="yellow"
          icon={<IconAlertTriangle size={18} />}
          title="Before you continue, check for client data"
        >
          <Stack gap="xs">
            <Text size="sm">
              Read through the skill and every file attached to it (scripts, spreadsheets, examples)
              and make sure there are no real client details anywhere:
            </Text>
            <List size="sm" spacing={4}>
              {CLIENT_DATA_TO_LOOK_FOR.map(item => (
                <List.Item key={item}>{item}</List.Item>
              ))}
            </List>
            <Text size="sm">If a skill needs example data, it must be made up.</Text>
          </Stack>
        </Alert>

        <Text size="sm">
          <Text span fw={600} inherit>
            Nothing checks this for you.
          </Text>{' '}
          You can unshare the skill later, which removes it from the library so no other firm can
          add it. Firms that have already added it keep their own copy, so unsharing is not a full
          undo, and this warning is the only safeguard before it is published.
        </Text>

        <Checkbox
          checked={hasChecked}
          onChange={event => setHasChecked(event.currentTarget.checked)}
          label="I have read this skill and opened every file attached to it, and neither the skill nor its files contain real client data"
        />

        {errorMessage && (
          <Text size="sm" c="red.7" role="alert">
            {errorMessage}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose} disabled={isSharing}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={!hasChecked} loading={isSharing}>
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
